import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["condition", "error", "hideExtraInputs", "onBlur", "onChange", "getIsUngated", "getTimezoneOptions", "useOnePanel"];
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import I18n from 'I18n';
import { getTransferableProps } from './filterInputProps';
import { fromSimpleDateToMoment, fromMomentToSimpleDate } from 'customer-data-filters-ui/utils/momentSimpleDateConversion';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { DateRange } from 'foundations-components/transitional/components/DateRange';
import { useEffect, useRef, useState } from 'react';
import TimezoneToggle from './TimezoneToggle';
import { TimeInput } from 'foundations-components/transitional/components/TimeInput';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isBetweenTimePointsWithTimeInstance } from '../../filterQueryFormat/operator/BetweenTimePointsWithTime';
import { ONE_MINUTE_BEFORE_MIDNIGHT, getCurrentTimeValue, getNewTimeValue, getTimeValueFromMoment } from '../../utils/timeValueUtils';
import { isNotBetweenTimePointsWithTimeInstance } from '../../filterQueryFormat/operator/NotBetweenTimePointsWithTime';
import styled from 'foundations-theming/utils';
import getConditionId from '../../utils/getConditionId';
import { FlexDateInputWithTimezoneWrapper } from '../../constants/inlineLayout';
import TimezoneDropdown from './TimezoneDropdown';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isBetweenOrNotBetweenWithTimeInstance = condition => isBetweenTimePointsWithTimeInstance(condition) || isNotBetweenTimePointsWithTimeInstance(condition);
const TimeInputBarWrapper = styled.div.withConfig({
  displayName: "FilterOperatorDateRangeInputMomentWithTime__TimeInputBarWrapper",
  componentId: "sc-18its4n-0"
})(["display:flex;"]);
const getStateFromValue = condition => {
  if (condition && isBetweenOrNotBetweenWithTimeInstance(condition)) {
    const {
      value,
      highValue
    } = condition;
    const timeValue = getTimeValueFromMoment(value);
    const highTimeValue = getTimeValueFromMoment(highValue);
    const isUsingTimeValue = highTimeValue < ONE_MINUTE_BEFORE_MIDNIGHT || timeValue > 0;
    return {
      isUsingTimeValue,
      isUTC: value.isUTC(),
      timeValue,
      highTimeValue
    };
  }
  return {
    isUsingTimeValue: false,
    isUTC: false,
    timeValue: 0,
    highTimeValue: ONE_MINUTE_BEFORE_MIDNIGHT
  };
};
const FilterOperatorDateRangeInputMomentWithTime = props => {
  const {
      condition,
      error,
      hideExtraInputs,
      onBlur,
      onChange,
      getIsUngated,
      getTimezoneOptions,
      useOnePanel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [isOpen, setIsOpen] = useState(false);
  const {
    isUsingTimeValue,
    isUTC,
    timeValue,
    highTimeValue
  } = getStateFromValue(condition);
  const onTimezoneChange = newUseUTC => {
    if (!condition) {
      return;
    }
    const {
      value: startDate,
      highValue: endDate
    } = condition;
    let newCondition;
    if (I18n.moment.isMoment(startDate) && I18n.moment.isMoment(endDate)) {
      const timezoneConverter = newUseUTC ? I18n.moment.utc : I18n.moment.portalTz;
      if (isUsingTimeValue) {
        //NOTE: as written, if a time has been added, then the date may change if timezone is toggled
        newCondition = condition.merge({
          value: timezoneConverter(startDate),
          highValue: timezoneConverter(endDate)
        });
      } else {
        newCondition = condition.merge({
          value: timezoneConverter(startDate.format('YYYY-MM-DD HH:mm:ss.SSS')),
          highValue: timezoneConverter(endDate.format('YYYY-MM-DD HH:mm:ss.SSS'))
        });
      }
      onChange(syntheticEvent(newCondition));
    }
  };
  const onChangeLocal = evt => {
    var _fromSimpleDateToMome, _fromSimpleDateToMome2;
    const startDate = (_fromSimpleDateToMome = fromSimpleDateToMoment(evt.target.value.startDate, isUTC)) !== null && _fromSimpleDateToMome !== void 0 ? _fromSimpleDateToMome : undefined;
    const endDate = (_fromSimpleDateToMome2 = fromSimpleDateToMoment(evt.target.value.endDate, isUTC)) !== null && _fromSimpleDateToMome2 !== void 0 ? _fromSimpleDateToMome2 : undefined;
    const startDateWithTime = getNewTimeValue(startDate, timeValue);
    const endDateWithTime = getNewTimeValue(endDate, highTimeValue);
    onChange(syntheticEvent(condition.merge({
      value: startDateWithTime,
      highValue: endDateWithTime
    })));
  };
  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (typeof onBlur === 'function' && isOpen === false && prevIsOpen.current === true) {
      onBlur();
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, onBlur]);
  const isError = error.get('error');
  const errorMessage = error.get('message');

  // @ts-expect-error we added types to `fromMomentToSimpleDate` after this was written
  const startDateInput = fromMomentToSimpleDate(condition.get('value'));
  // @ts-expect-error we added types to `fromMomentToSimpleDate` after this was written
  const endDateInput = fromMomentToSimpleDate(condition.get('highValue'));
  const inputValue = {
    startDate: startDateInput,
    endDate: endDateInput
  };
  const handleAddTime = () => {
    if (condition && isBetweenOrNotBetweenWithTimeInstance(condition)) {
      const {
        value: startDate,
        highValue: endDate
      } = condition;
      const newTimeValue = getCurrentTimeValue(isUTC);
      onChange(syntheticEvent(condition.merge({
        value: getNewTimeValue(startDate, newTimeValue),
        highValue: getNewTimeValue(endDate, newTimeValue)
      })));
    }
  };
  const handleRemoveTime = () => {
    if (condition && isBetweenOrNotBetweenWithTimeInstance(condition)) {
      const {
        value: startDate,
        highValue: endDate
      } = condition;
      onChange(syntheticEvent(condition.merge({
        value: getNewTimeValue(startDate, 0),
        highValue: getNewTimeValue(endDate, ONE_MINUTE_BEFORE_MIDNIGHT)
      })));
    }
  };
  const handleTimeChange = evt => {
    if (condition && isBetweenOrNotBetweenWithTimeInstance(condition)) {
      const {
        value: startDate,
        highValue: endDate
      } = condition;
      const newTimeValue = evt.target.value;
      if (startDate.format('YYYY-MM-DD') !== endDate.format('YYYY-MM-DD') || newTimeValue <= highTimeValue) {
        onChange(syntheticEvent(condition.merge({
          value: getNewTimeValue(startDate, newTimeValue)
        })));
      }
    }
  };
  const handleHighTimeChange = evt => {
    if (condition && isBetweenOrNotBetweenWithTimeInstance(condition)) {
      const {
        value: startDate,
        highValue: endDate
      } = condition;
      const newTimeValue = evt.target.value;
      if (startDate.format('YYYY-MM-DD') !== endDate.format('YYYY-MM-DD') || newTimeValue >= timeValue) {
        onChange(syntheticEvent(condition.merge({
          highValue: getNewTimeValue(endDate, newTimeValue)
        })));
      }
    }
  };
  const transferableProps = getTransferableProps(rest);
  const {
    ['data-selenium-test']: dataSeleniumTest
  } = transferableProps;
  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const disabled = transferableProps.disabled;
  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const readOnly = transferableProps.readOnly;
  const TimeInputBar = /*#__PURE__*/_jsxs(TimeInputBarWrapper, {
    children: [/*#__PURE__*/_jsx(TimeInput, {
      "data-selenium-test": dataSeleniumTest,
      disabled: disabled,
      readOnly: readOnly,
      onChange: handleTimeChange,
      value: timeValue
    }), /*#__PURE__*/_jsx(View, {
      flexGrow: 0,
      alignSelf: "center",
      flexBasis: "auto",
      paddingBlock: "0",
      paddingInline: "250",
      children: /*#__PURE__*/_jsx(Text, {
        visuallyHidden: true,
        children: I18n.text('ui.dateRange.to')
      })
    }), /*#__PURE__*/_jsx(TimeInput, {
      "data-selenium-test": dataSeleniumTest,
      disabled: disabled,
      readOnly: readOnly,
      onChange: handleHighTimeChange,
      value: highTimeValue
    })]
  });
  const InputWrapper = useOnePanel ? FlexDateInputWithTimezoneWrapper : 'div';
  const TimezoneSelector = useOnePanel ? TimezoneDropdown : TimezoneToggle;
  return /*#__PURE__*/_jsxs(InputWrapper, {
    children: [/*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
      error: isError,
      validationMessage: isError ? errorMessage : null,
      children: /*#__PURE__*/_jsx(DateRange, {
        "data-selenium-test": dataSeleniumTest,
        disabled: disabled,
        readOnly: readOnly,
        onChange: onChangeLocal,
        onOpenChange: evt => setIsOpen(evt.target.value),
        value: inputValue
      })
    }), isUsingTimeValue ? /*#__PURE__*/_jsxs(View, {
      marginBlock: "200",
      as: "div",
      children: [TimeInputBar, /*#__PURE__*/_jsx(Button, {
        onClick: handleRemoveTime,
        use: "transparent",
        size: "sm",
        responsive: false,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterOperatorDateInputMomentWithTime.removeTime"
        })
      })]
    }) : /*#__PURE__*/_jsx(Button, {
      onClick: handleAddTime,
      use: "transparent",
      size: "sm",
      responsive: false,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterOperatorDateInputMomentWithTime.addTime"
      })
    }), !hideExtraInputs && /*#__PURE__*/_jsx(TimezoneSelector, {
      conditionId: getConditionId(condition),
      timezoneOptions: getTimezoneOptions(condition.field),
      fieldType: condition.field ? condition.field.get('type') : undefined,
      useUTC: isUTC,
      onChange: onTimezoneChange,
      getIsUngated: getIsUngated
    })]
  });
};
export default FilterOperatorDateRangeInputMomentWithTime;